import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError } from '../../lib/api';
import { Loader2, Users, Ticket, IndianRupee, CalendarClock } from 'lucide-react';

const RANGES = [{ d: 7, label: '7D' }, { d: 30, label: '30D' }, { d: 90, label: '90D' }];
const PURPOSE_COLORS = { membership: '#0891b2', booking: '#6366f1', custom: '#f59e0b', other: '#94a3b8' };
const STATUS_COLORS = { active: '#16a34a', frozen: '#3b82f6', expired: '#9ca3af', cancelled: '#ef4444', unknown: '#cbd5e1' };
const SERVICE_COLORS = ['#0891b2', '#14b8a6', '#ec4899', '#8b5cf6', '#f59e0b'];

const inr = (n) => `\u20B9${(n || 0).toLocaleString('en-IN')}`;
const shortDate = (d) => (d ? d.slice(5) : d);

const KpiCard = ({ label, value, sub, icon: Icon, gradient, testid }) => (
  <Card className={`border-0 shadow-sm bg-gradient-to-r ${gradient} text-white`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/80 text-sm">{label}</p>
          <p className="text-2xl font-bold" data-testid={testid}>{value}</p>
          {sub && <p className="text-white/70 text-xs mt-0.5">{sub}</p>}
        </div>
        <Icon className="h-7 w-7 opacity-80" />
      </div>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, action, children, testid }) => (
  <Card className="shadow-md border-0 bg-white" data-testid={testid}>
    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center justify-between">{title}{action}</CardTitle></CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const AnalyticsPage = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/analytics/summary', { params: { days } });
      setData(res.data);
    } catch (err) {
      toast({ title: 'Failed to load analytics', description: formatApiError(err), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [days, toast]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="flex justify-center py-32" data-testid="analytics-loading"><Loader2 className="h-10 w-10 animate-spin text-blue-500" /></div>;
  }

  const ov = data.overview;
  const purposeData = Object.entries(data.by_purpose || {}).map(([name, value]) => ({ name, value }));
  const totalBookingsRange = ov.bookings_in_range;

  const rangeSelector = (
    <div className="inline-flex bg-white rounded-lg p-1 shadow-sm border border-gray-100" data-testid="range-selector">
      {RANGES.map((r) => (
        <button key={r.d} onClick={() => setDays(r.d)}
          className={`px-3 py-1.5 rounded-md text-xs font-medium ${days === r.d ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
          data-testid={`range-${r.d}`}>
          {r.label}
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500 text-sm mt-1">Revenue, bookings & attendance trends</p>
        </div>
        {rangeSelector}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Total Revenue" value={inr(ov.total_revenue)} sub={`This month ${inr(ov.month_revenue)}`} icon={IndianRupee} gradient="from-emerald-600 to-green-600" testid="kpi-revenue" />
        <KpiCard label="Total Members" value={ov.total_members} sub={`${totalBookingsRange} bookings / ${days}d`} icon={Users} gradient="from-blue-600 to-cyan-600" testid="kpi-members" />
        <KpiCard label="Active Memberships" value={ov.active_memberships} sub={`${ov.frozen_memberships} frozen`} icon={Ticket} gradient="from-indigo-600 to-purple-600" testid="kpi-active-memberships" />
        <KpiCard label="Expiring (30d)" value={ov.expiring_30d} sub="memberships ending soon" icon={CalendarClock} gradient="from-amber-500 to-orange-500" testid="kpi-expiring" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue trend */}
        <ChartCard title="Revenue Trend" testid="chart-revenue">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.revenue_trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0891b2" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#0891b2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} minTickGap={20} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => inr(v)} labelFormatter={shortDate} />
              <Area type="monotone" dataKey="revenue" stroke="#0891b2" strokeWidth={2} fill="url(#revGrad)" name="Revenue" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Bookings trend */}
        <ChartCard title="Bookings: Online vs Walk-in" testid="chart-bookings">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.bookings_trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} minTickGap={20} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip labelFormatter={shortDate} />
              <Legend />
              <Bar dataKey="online" stackId="b" fill="#3b82f6" name="Online" radius={[0, 0, 0, 0]} />
              <Bar dataKey="walkin" stackId="b" fill="#a855f7" name="Walk-in" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Attendance trend */}
        <ChartCard title="Attendance Trend" testid="chart-attendance">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.attendance_trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} minTickGap={20} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip labelFormatter={shortDate} />
              <Legend />
              <Line type="monotone" dataKey="checkins" stroke="#14b8a6" strokeWidth={2} dot={false} name="Check-ins" />
              <Line type="monotone" dataKey="unique" stroke="#f59e0b" strokeWidth={2} dot={false} name="Unique swimmers" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Revenue by purpose */}
        <ChartCard title="Revenue by Type" testid="chart-purpose">
          {purposeData.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">No revenue yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={purposeData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {purposeData.map((e) => (<Cell key={e.name} fill={PURPOSE_COLORS[e.name] || '#94a3b8'} />))}
                </Pie>
                <Tooltip formatter={(v) => inr(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Service breakdown */}
        <ChartCard title="Revenue by Service" testid="chart-services">
          {data.service_breakdown.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">No bookings in range</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.service_breakdown} layout="vertical" margin={{ top: 10, right: 20, left: 30, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
                <Tooltip formatter={(v) => inr(v)} />
                <Bar dataKey="revenue" radius={[0, 4, 4, 0]} name="Revenue">
                  {data.service_breakdown.map((e, i) => (<Cell key={e.service} fill={SERVICE_COLORS[i % SERVICE_COLORS.length]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Membership distribution */}
        <ChartCard title="Membership Status" testid="chart-memberships">
          {data.membership_distribution.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">No memberships yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.membership_distribution} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.status} (${e.count})`} labelLine={false}>
                  {data.membership_distribution.map((e) => (<Cell key={e.status} fill={STATUS_COLORS[e.status] || '#cbd5e1'} />))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {data.overview.mock_mode && (
        <div className="mt-4">
          <Badge className="bg-amber-100 text-amber-800" data-testid="analytics-mock-badge">Revenue figures include Razorpay Test/Mock payments</Badge>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;
